import { getHttpClientErrorMessage, getHttpServerErrorMessage } from '@/utils'
import { endpoints } from './endpoints'

// Define specific types for different content types
type JsonValue = string | number | boolean | null | JsonObject | JsonArray
type JsonObject = { [key: string]: JsonValue }
type JsonArray = JsonValue[]

// More specific header types
type AcceptHeader = 'application/json' | 'application/pdf' | 'application/xml' | 'application/csv' | 'application/vnd.ms-excel' | 'application/zip' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
type ContentTypeHeader = 'application/json' | 'application/x-www-form-urlencoded' | 'multipart/form-data' | 'text/plain'

type TypeFetchOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  mode?: 'cors' | 'no-cors' | 'same-origin'
  cache?: 'default' | 'no-cache' | 'no-store' | 'reload' | 'force-cache' | 'only-if-cached'
  credentials?: 'same-origin' | 'include' | 'omit'
  headers?: Record<string, string | undefined> & {
    'Accept'?: AcceptHeader
    'Content-Type'?: ContentTypeHeader
    'Authorization'?: string
    'DY-Access-Token'?: string
    'DY-Lang'?: string
  }
  redirect?: 'follow' | 'manual' | 'error'
  referrerPolicy?: 'no-referrer' | 'no-referrer-when-downgrade' | 'origin' | 'origin-when-cross-origin' | 'same-origin' | 'strict-origin' | 'strict-origin-when-cross-origin' | 'unsafe-url'
  body?: BodyInit | null
  signal?: AbortSignal
  next?: {
    revalidate?: number
    tags?: string[]
  }
}
// Any body that you want to add to your request: this can be a Blob, an ArrayBuffer, a TypedArray, a DataView, a FormData, a URLSearchParams, string object or literal

const defaultOptions:TypeFetchOptions = {
  method: 'GET',
  mode: 'cors',
  cache: 'default',
  credentials: 'same-origin',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
  body: null
}

type TypeQuery = {
  vet_id?: string | number
  user_id?: string | number
  commission_id?: string | number
  customer_id?: string | number
  recommendation_id?: string | number
  email?: string
  token?: string
  searchParams?: {
    q?: string,
    status?: 'ready' | 'pending' | 'active',
    sort?: string,
    direction?: 'asc' | 'desc',
    page?: number
  }
  order_id?: string | number
}


export type TypeBlob = { blob: Blob, serverFilename?: string }
type TypeFetchApi = (urlNamespace: keyof typeof endpoints, query?: TypeQuery, options?: TypeFetchOptions) => Promise<Response | TypeBlob>


export const fetchAPI:TypeFetchApi = async (endpointNamespace, query = {}, fetchOptions) => {
  if(!endpointNamespace || !endpoints[endpointNamespace]) {
    throw new Error(`api/endpoints.ts -> No endpoint with namespace: '${endpointNamespace}'`)
  }

  const url = endpoints[endpointNamespace](query)
  const options = !fetchOptions ? defaultOptions : {...defaultOptions, ...fetchOptions }
  const body = options.body ?? null

  const res:Response = await fetch(url, {...options, body })
  switch(res.status) {
    case 204:
    case 205:
      // those status do not return a body
      return res
    case 200:
    case 201:
      if (typeof window === 'undefined') return res
      if (endpointNamespace.indexOf('download') !== -1) {
        const disposition = res.headers.get('Content-Disposition')
        const filename = getFileNameFromContentDisposition(disposition)

        const blob = await res.blob()
        return { blob, serverFilename: filename }
      }

      const data = await res.json()
      return Response.json(data)
    case 400:
    case 401:
    case 402:
    case 403:
    case 404:
    case 405:
    case 406:
    case 407:
    case 408:
    case 409:
    case 410:
    case 411:
    case 412:
    case 413:
    case 414:
    case 415:
    case 416:
    case 417:
    case 421:
    case 422:
      console.log(`❌ DY '${endpointNamespace}' | ${res.status} - ${getHttpClientErrorMessage(res.status)}`)
      return res
    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
    case 505:
    case 506:
    case 507:
    case 508:
    case 509:
    case 510:
    case 511:
      throw new Error(`❌ DY '${endpointNamespace}' | ${res.status} - ${getHttpServerErrorMessage(res.status)}`)
    default:
      console.log('res.status', res.status)
      throw new Error('❌ DY Unhandled 😭 | Something went wrong with the fetch')
  }
}


/**
 * Extracts the filename (decoded) from a Content-Disposition header value.
 *
 * @param contentDisposition The full header string (e.g. from res.headers.get('Content-Disposition'))
 * @returns The decoded filename, or null if none found.
 */
function getFileNameFromContentDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null

  // 1. Try to match the RFC5987/6266 extended form: filename*=<charset>''<percent-encoded-filename>
  const extMatch = contentDisposition.match(/filename\*\s*=\s*[^']*''([^;]+)/i)
  if (extMatch && extMatch[1]) {
    // The value after '' is percent-encoded (e.g. 800863_%20test..._%C3%B3rico.xlsx)
    try {
      return decodeURIComponent(extMatch[1])
    } catch {
      // fallback if decode fails
      return extMatch[1]
    }
  }

  // 2. Fallback to the simple filename="..."; strip quotes if present
  const simpleMatch = contentDisposition.match(/filename\s*=\s*\"?([^\";]+)\"?/i)
  if (simpleMatch && simpleMatch[1]) {
    return simpleMatch[1]
  }

  return null
}
